/** 系统常量 */

export const APP_TITLE = import.meta.env.VITE_APP_TITLE ?? 'React Admin';

/** 当前环境（development / test / staging / production） */
export const APP_ENV = import.meta.env.VITE_APP_ENV ?? 'development';
export const IS_PROD = APP_ENV === 'production';

/**
 * localStorage keys
 *
 * ⚠️ 这里**没有 token**：会话是服务端下发的 httpOnly Cookie（`MUOU_ADMIN`），前端读不到
 * 也写不了，没有任何凭证需要存。原来的 `TOKEN_KEY` 已随 Bearer 方案一并删除。
 */
export const SETTINGS_KEY = 'react_admin_settings';
/** 「记住我」只存总会前缀与邮箱，**不存密码**（见 pages/Login/index.tsx） */
export const LOGIN_INFO_KEY = 'react_admin_login_info';
export const LOCALE_KEY = 'react_admin_locale';
// 这里原来还有三个键，随「双会话配对」一并删除（2026-10-08）：
//   TENANT_CODE_KEY（记「单体会话在哪一家」，用来配对两套会话）
//   IAM_ONLY_KEY    （记「这次只登上了 iam」）
//   IAM_STALE_KEY   （记「浏览器里那份 iam 会话不可信」）
// 三者的前提都是「单体会话与 iam 会话并存」——IAM-only 之后浏览器里只有 iam 那一条会话，
// 登录态不再需要任何本地记号，整份状态每次启动都由 getIamSession 现问（见 store/auth.ts）。

// 这里原来有一个 `WHITE_LIST = ['/login', '/password/change']`（「无需登录即可访问的路由」）。
// 2026-08-31 删除：**全仓没有任何消费者**，它从来只是被定义、没有守卫在查它，留着只会让人
// 以为改这个数组就能放行一条路由。免登录是靠路由结构实现的——`/login` 与 `/password/change`
// 在 `router/index.tsx` 里与 `AuthGuard` 那棵子树**平级**，压根不经过守卫。
// 要再加一条免登录页，同样是把它挂在 AuthGuard 之外，不是往某个数组里加字符串。

/** 后台对外提供的界面语言；三语业务内容的维护不依赖这份清单。 */
export const LOCALES = [
  { label: '繁體中文', value: 'zh-HK' },
  { label: 'English', value: 'en-US' },
];

/**
 * 组织会务：后端路由键 → 前端路径
 *
 * 会务待办的 `OrgTodoVO.to`、机构信息指标格的 `OrgMetricVO.to` 给的都是 `org.info`
 * 这类**键**，不是 URL——后端不该知道前端的路由长什么样。映射只此一处，加页面时
 * 这里跟着加；键对不上就拿不到路径，调用方据此把「去办」按钮置灰而不是跳到空白页。
 */
export const ORG_ROUTE_PATH: Record<string, string> = {
  'org.notice': '/org/notice',
  'org.meeting': '/org/meeting',
  'org.motion': '/org/motion',
  'org.todo': '/org/todo',
  'org.info': '/org/info',
  'org.dept': '/org/dept',
  'org.job': '/org/job',
  'org.cmt': '/org/cmt',
  'org.committee': '/org/committee',
  'org.cmtkind': '/org/cmtkind',
  'org.duty': '/org/duty',
};

/**
 * 身份状态编码清单（API-11-241 的 `identityStates`，取判定后的状态编码）。
 * 会籍与注册身份共用一套编码、叫法按段的种类取（11-02 第一节），所以同一个编码
 * 在两类身份上读法不同。这里只留编码，显示名走 `member.identity.status.*` 词条
 * （`pages/muou/member/identityStatus.ts`）——写在这里的中文会被页面直接渲染成中文。
 */
export const IDENTITY_STATE_CODES = [
  'active',
  'pending_pay',
  'expired',
  'lapsed',
  'frozen',
  'quit',
  'revoked',
] as const;

/** StatusTag 按中文业务词查色，这里只给色号词，显示名走词条 */
export const IDENTITY_STATE_COLOR_WORD: Record<string, string> = {
  active: '在籍',
  pending_pay: '待生效',
  expired: '已过期',
  lapsed: '已失效',
  frozen: '已冻结',
  quit: '已退会',
  revoked: '已吊销',
};

/**
 * 会籍四态的**取值**（`MemberState` 的运行时副本）。
 *
 * 服务端下发的是中文枚举值，页面拿它做判断（`status === MEMBER_STATE.expired`），
 * 不直接渲染。集中在这里，页面里不再散落中文字面量。
 */
export const MEMBER_STATE = {
  active: '在籍',
  frozen: '已冻结',
  expired: '已过期',
  left: '已退会',
} as const;

/**
 * 入会 / 续期申请表单快照（`ApplicationVO.payload`）的字段名。
 * 后端快照用中文字面量键，这里只用于取值，不渲染。
 */
export const MEMBER_APP_PAYLOAD_KEY = {
  intendedClub: '意向属会',
  memberType: '会员类型',
  originalExpiry: '原到期日',
} as const;

/**
 * 订单细分状态九档（状态列实际显示的那一层，R2）：编码取 OS1.SUB，与后端 OrderSubStatus 逐字对齐。
 * 值是词条 id，显示名由渲染处 formatMessage。
 */
export const ORDER_SUB_STATUS_LABEL: Record<string, string> = {
  refund_audit: 'order.finance.status.refund_audit',
  refund_paying: 'order.finance.status.refund_paying',
  // 细分档的「待审核凭证」与底层态 evidence_review 同一句话，共用词条
  verifying: 'order.finance.status.evidence_review',
  refunded: 'order.finance.status.refunded',
  cancelled: 'order.finance.status.cancelled',
  closed_sys: 'order.finance.status.closed_sys',
  paid: 'order.finance.status.paid',
  expired_unpaid: 'order.finance.status.expired_unpaid',
  pending: 'order.finance.status.pending',
};

/** StatusTag 按中文业务词查色，这里只给色号词，显示名走词条 */
export const ORDER_SUB_STATUS_COLOR_WORD: Record<string, string> = {
  refund_audit: '退款审核中',
  refund_paying: '退款处理中',
  verifying: '待审核凭证',
  refunded: '已退款',
  cancelled: '已取消',
  closed_sys: '系统关闭',
  paid: '已支付',
  expired_unpaid: '过期未支付',
  pending: '待支付',
};

/**
 * 订单底层十态（fin_order.status 原值），只进状态标签的悬停「系统状态」那一格。
 * 值是词条 id，显示名由渲染处 formatMessage。
 */
export const ORDER_RAW_STATUS_LABEL: Record<string, string> = {
  pending: 'order.finance.status.pending',
  evidence_review: 'order.finance.status.evidence_review',
  paid: 'order.finance.status.paid',
  expired: 'order.finance.status.expired',
  cancelled: 'order.finance.status.cancelled',
  closed: 'order.finance.status.closed',
  refunded: 'order.finance.status.refunded',
  refunding: 'order.finance.status.refunding',
};
